import React, { useState } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, ExternalLink, Download } from "lucide-react";
import { arrayUnion, collection, doc, getDocs, query, updateDoc, where } from "firebase/firestore";
import InfoModal from "../../components/InfoModal";
import { auth, db } from "../../firebase";
import { judgementDetailToCaseDetails } from "./ngtCaseMapper";
import { uploadNgtOrderPdfs, normalizeNgtUrl } from "./ngtFileSync";
import { statusPillSolid } from "../../styles/statusPill";

/**
 * Detail page for a single NGT judgement/order. Reached from
 * <NGTJudgementList /> after it fetches the detail.
 *
 * Expects location.state: { detail, row, zone, filingNumber }, where `detail`
 * is the flat key/value object scraped by the backend, e.g.
 *   { Filing_Number, Filing_Date, Party_Name, Case_Number, Case_Status, ...,
 *     pdfLinks:[{ type, url }] }
 * Keys are dynamic, so fields are rendered generically.
 */

// Preferred display order; any other keys are appended after these.
const FIELD_ORDER = [
  "Case_Number",
  "Filing_Number",
  "Filing_Date",
  "Registered_On",
  "Last_Listed",
  "Party_Name",
  "Petitioner_Advocate(s)",
  "Respondent_Advocate(s)",
  "Act",
  "Case_Status",
];

const prettyLabel = (key) => key.replace(/_/g, " ").trim();

function NGTJudgementDetails() {
  const location = useLocation();
  const navigate = useNavigate();

  const { detail, row = {}, zone, filingNumber } = location.state || {};

  const [exporting, setExporting] = useState(false);
  const [infoModal, setInfoModal] = useState({ open: false, title: "", message: "" });

  // Export this judgement/order INTO an already-exported case.
  //
  // A judgement order is not a standalone case — it can only be attached to a
  // case the user has already exported from Case Status. We match that case by
  // case number OR filing/diary number across pending + disposed; if none is
  // found we refuse and tell the user to export the case first. On a match we
  // append the order PDFs to the case's `ngtOrders` array (deduped by URL) and
  // mirror them into the case's Files "Orders" folder — no new case is created.
  const handleExportCase = async () => {
    const user = auth.currentUser;
    if (!user) {
      setInfoModal({ open: true, title: "Not signed in", message: "User not authenticated." });
      return;
    }
    try {
      setExporting(true);

      const { caseInfo } = judgementDetailToCaseDetails(detail, row);
      const caseNo = caseInfo.caseNumber || row.caseNumber || "";
      const filing = caseInfo.filingNumber || row.diaryNumber || filingNumber || "";

      if (!caseNo && !filing) {
        setInfoModal({
          open: true,
          title: "Cannot match case",
          message: "This order has no case number or filing number to match against your saved cases.",
        });
        return;
      }

      // Find the matching already-exported case (by caseNo, then filingNumber)
      // across pending / disposed.
      let matched = null; // { id, collection, data }
      for (const coll of ["pending", "disposed"]) {
        if (matched) break;
        const queries = [];
        if (caseNo) {
          queries.push(
            query(collection(db, coll), where("owner", "==", user.uid), where("caseNo", "==", caseNo))
          );
        }
        if (filing) {
          queries.push(
            query(collection(db, coll), where("owner", "==", user.uid), where("filingNumber", "==", filing))
          );
        }
        for (const q of queries) {
          const snap = await getDocs(q);
          if (!snap.empty) {
            matched = { id: snap.docs[0].id, collection: coll, data: snap.docs[0].data() };
            break;
          }
        }
      }

      if (!matched) {
        setInfoModal({
          open: true,
          title: "Case not exported yet",
          message: `This order can only be exported once its case is in your profile. Please export case ${
            caseNo || filing
          } from the "Case Status" tab first, then export this order again.`,
        });
        return;
      }

      // Build order entries from the judgement's PDF links.
      const pdfLinks = Array.isArray(detail.pdfLinks) ? detail.pdfLinks : [];
      const orderDate = caseInfo.lastListed || row.dateOfOrder || "";
      const orderEntries = pdfLinks
        .filter((p) => p && p.url)
        .map((p) => ({
          orderDate,
          url: p.url,
          type: p.type || "order",
          source: "ngt-judgement",
          addedAt: new Date().toISOString(),
        }));

      if (orderEntries.length === 0) {
        setInfoModal({
          open: true,
          title: "No order document",
          message: "No order PDF was found on this judgement to export.",
        });
        return;
      }

      // Dedupe against orders already attached to the case (normalised URL).
      const existing = Array.isArray(matched.data.ngtOrders) ? matched.data.ngtOrders : [];
      const existingUrls = new Set(existing.map((o) => normalizeNgtUrl(o.url)));
      const fresh = orderEntries.filter((o) => !existingUrls.has(normalizeNgtUrl(o.url)));

      if (fresh.length === 0) {
        setInfoModal({
          open: true,
          title: "Already attached",
          message: `These order documents are already attached to case ${matched.data.caseNo || caseNo}.`,
        });
        return;
      }

      const caseRef = doc(db, matched.collection, matched.id);
      await updateDoc(caseRef, { ngtOrders: arrayUnion(...fresh) });

      // Mirror the orders into the case's Files "Orders" folder + files[].
      // uploadNgtOrderPdfs dedupes against files already on the case, so any
      // order whose PDF is already present is skipped rather than duplicated.
      const { uploaded, skipped } = await uploadNgtOrderPdfs({
        user,
        caseId: matched.id,
        caseNo: matched.data.caseNo || caseNo,
        caseCollection: matched.collection,
        listingHistory: fresh.map((o, i) => ({
          sNo: i + 1,
          dateOfListing: o.orderDate,
          dateOfUpload: "",
          coram: [],
          pdfPath: o.url,
        })),
      });

      setInfoModal({
        open: true,
        title: "Order exported",
        message: `${uploaded} order document(s) attached to case ${
          matched.data.caseNo || caseNo
        }${skipped ? ` (${skipped} already present, skipped)` : ""}. View them under the case's Orders tab and in Files → Orders.`,
      });
    } catch (err) {
      console.error("NGT judgement export error:", err);
      setInfoModal({ open: true, title: "Export failed", message: `Error exporting order: ${err.message}` });
    } finally {
      setExporting(false);
    }
  };

  if (!detail) {
    return (
      <>
                <div className="relative min-h-screen flex flex-col items-center justify-center text-white">
          <div className="mb-4"><FindCaseStepper current={4} /></div>
          <p className="mb-4">No case details to display.</p>
          <Link
            to="/portal/fetchfromngt"
            className="bg-yellow-500 text-[#060125] rounded py-2 px-6 font-bold hover:bg-yellow-400"
          >
            Back to Search
          </Link>
        </div>
      </>
    );
  }

  const { pdfLinks = [], ...fields } = detail;

  // Build ordered list of [key, value] entries (preferred order first).
  const orderedKeys = [
    ...FIELD_ORDER.filter((k) => fields[k] != null && fields[k] !== ""),
    ...Object.keys(fields).filter(
      (k) => !FIELD_ORDER.includes(k) && fields[k] != null && fields[k] !== ""
    ),
  ];

  const title = fields.Case_Number || row?.caseNumber || "Case Details";
  const status = fields.Case_Status || row?.status || "";

  return (
    <>
                  
      <div className="relative min-h-screen flex flex-col">
          <div className="mb-4"><FindCaseStepper current={4} /></div>
        <main className="px-4 md:px-16 py-8">
          {/* Header */}
          <div className="relative z-10 flex items-center justify-between p-4 gap-3 flex-wrap">
            <button onClick={() => navigate(-1)} className="flex items-center md:ml-12" type="button">
              <ArrowLeft style={{ color: "#334155" }} />
              <h1 className="text-yellow-500 ml-4 font-bold text-2xl">Judgement / Order Details</h1>
            </button>
            <button
              onClick={handleExportCase}
              disabled={exporting}
              className={`bg-yellow-500 text-gray-900 py-2.5 px-8 rounded-lg shadow-sm flex items-center justify-center font-bold hover:bg-gray-200 ${
                exporting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <span className="mr-2">☁</span>
              {exporting ? "Importing..." : "Import Case"}
            </button>
          </div>

          <div className="relative z-10 mt-2 mb-12 max-w-5xl mx-auto w-full space-y-6">
            {/* Title card */}
            <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <h2 className="text-white font-bold text-lg">{title}</h2>
                  {fields.Party_Name && (
                    <p className="text-slate-600 text-sm mt-1">{fields.Party_Name}</p>
                  )}
                  {(zone || filingNumber) && (
                    <p className="text-slate-500 text-xs mt-1">
                      {zone ? `Bench: ${zone}` : ""}
                      {zone && filingNumber ? " · " : ""}
                      {filingNumber ? `Filing No: ${filingNumber}` : ""}
                    </p>
                  )}
                </div>
                {status && (
                  <span
                    className={`self-start px-3 py-1 rounded-full text-xs font-semibold text-white ${statusPillSolid(
                      status
                    )}`}
                  >
                    {status}
                  </span>
                )}
              </div>
            </div>

            {/* Case information */}
            <div className="bg-white border border-slate-200 shadow-sm rounded-2xl overflow-hidden">
              <h3 className="text-yellow-500 font-bold text-base px-5 py-3 border-b border-white/10">
                Case Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 p-5">
                {orderedKeys.map((key) => (
                  <div
                    key={key}
                    className="flex justify-between gap-4 py-2 border-b border-white/5 text-sm"
                  >
                    <span className="text-slate-500">{prettyLabel(key)}</span>
                    <span className="text-white text-right font-medium">{fields[key]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Orders / PDFs */}
            <div className="bg-white border border-slate-200 shadow-sm rounded-2xl overflow-hidden">
              <h3 className="text-yellow-500 font-bold text-base px-5 py-3 border-b border-white/10 flex items-center gap-2">
                <FileText className="h-4 w-4" /> Orders / Judgement PDFs
              </h3>
              {pdfLinks.length === 0 ? (
                <p className="text-slate-500 text-sm p-5">No order PDFs available for this case.</p>
              ) : (
                <ul className="divide-y divide-white/5">
                  {pdfLinks.map((pdf, i) => (
                    <li key={i} className="flex items-center justify-between px-5 py-3">
                      <span className="text-white text-sm flex items-center gap-2">
                        <FileText className="h-4 w-4 text-yellow-400" />
                        Order Document {i + 1}
                        <span className="text-gray-500 text-xs">({pdf.type})</span>
                      </span>
                      <a
                        href={pdf.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 bg-yellow-500 text-[#060125] rounded py-1 px-3 text-xs font-bold hover:bg-yellow-400"
                      >
                        <Download className="h-3.5 w-3.5" /> Open
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </main>
      </div>

      <InfoModal
        isOpen={infoModal.open}
        onClose={() => setInfoModal({ open: false, title: "", message: "" })}
        title={infoModal.title}
        message={infoModal.message}
      />
    </>
  );
}

export default NGTJudgementDetails;
